import { ColDef } from "ag-grid-community";
import {
  IAGAssetMarginReportDataRow,
  IAGBalanceUpdateDataRow,
  IAGOrderEventDataRow,
  IAGPositionMarginReportDataRow,
  IAGPositionUpdateDataRow,
  IAGTradeEventDataRow,
  IAGTransferEventDataRow,
} from "@pulse-ui/types/components/AGTable";

// Cell Renderers
import SideCell from "../AGCellRenderers/SideCell";
import OrderStatusCell from "../AGCellRenderers/OrderStatusCell";
import {
  currencyFormatter,
  customNumericSortFunc,
  numericFormatterNoPrecision,
} from "../../utils/helper_functions";
import VenueCell from "../AGCellRenderers/VenueCell";
import TransferStatusCell from "../AGCellRenderers/TransferStatusCell";

export const initialColDefs: Map<
  string,
  ColDef<
    | IAGOrderEventDataRow
    | IAGPositionUpdateDataRow
    | IAGBalanceUpdateDataRow
    | IAGAssetMarginReportDataRow
    | IAGPositionMarginReportDataRow
    | IAGTradeEventDataRow
    | IAGTransferEventDataRow
  >
> = new Map([
  [
    "account_wallet",
    {
      headerName: "Account Wallet",
      field: "account_wallet",
      cellClass: "ag-center-aligned-cell",
    },
  ],
  [
    "src_account_wallet",
    {
      headerName: "Source Account Wallet",
      field: "src_account_wallet",
      cellClass: "ag-center-aligned-cell",
    },
  ],
  [
    "dest_account_wallet",
    {
      headerName: "Destination Account Wallet",
      field: "dest_account_wallet",
      cellClass: "ag-center-aligned-cell",
    },
  ],
  [
    "asset",
    {
      headerName: "Asset",
      field: "asset",
      cellClass: "ag-center-aligned-cell",
    },
  ],
  [
    "asset_margin_ratio",
    {
      headerName: "Asset Margin Ratio",
      field: "asset_margin_ratio",
      cellClass: "ag-center-aligned-cell",
    },
  ],
  [
    "available_balance",
    {
      headerName: "Available Balance",
      field: "available_balance",
      valueFormatter: numericFormatterNoPrecision,
      comparator: customNumericSortFunc,
      cellClass: "ag-center-aligned-cell",
    },
  ],
  [
    "avg_px",
    {
      headerName: "$Average Price",
      field: "avg_px",
      valueFormatter: (params: any) => currencyFormatter(params, "$"),
      comparator: customNumericSortFunc,
      cellClass: "ag-center-aligned-cell",
    },
  ],
  [
    "blockchain_wallet",
    {
      headerName: "Blockchain Wallet",
      field: "blockchain_wallet",
      cellClass: "ag-center-aligned-cell",
    },
  ],
  [
    "src_blockchain_wallet",
    {
      headerName: "Source Blockchain Wallet",
      field: "src_blockchain_wallet",
      cellClass: "ag-center-aligned-cell",
    },
  ],
  [
    "dest_blockchain_wallet",
    {
      headerName: "Destination Blockchain Wallet",
      field: "dest_blockchain_wallet",
      cellClass: "ag-center-aligned-cell",
    },
  ],
  [
    "clearing_date",
    {
      headerName: "Date",
      field: "clearing_date",
      cellClass: "ag-center-aligned-cell",
    },
  ],
  [
    "client_order_id",
    {
      headerName: "Client Order ID",
      field: "client_order_id",
      cellClass: "ag-center-aligned-cell",
    },
  ],
  [
    "contract_size",
    {
      headerName: "Contract Size",
      field: "contract_size",
      cellClass: "ag-center-aligned-cell",
    },
  ],
  [
    "cum_qty",
    {
      headerName: "Cummulative Quantity",
      field: "cum_qty",
      valueFormatter: numericFormatterNoPrecision,
      comparator: customNumericSortFunc,
      cellClass: "ag-center-aligned-cell",
    },
  ],
  [
    "current_date",
    {
      headerName: "Current Date",
      field: "current_date",
      cellClass: "ag-center-aligned-cell",
    },
  ],
  [
    "customer_account",
    {
      headerName: "Customer Account",
      field: "customer_account",
      cellClass: "ag-center-aligned-cell",
    },
  ],
  [
    "src_customer_account",
    {
      headerName: "Source Customer Account",
      field: "src_customer_account",
      cellClass: "ag-center-aligned-cell",
    },
  ],
  [
    "dest_customer_account",
    {
      headerName: "Destination Customer Account",
      field: "dest_customer_account",
      cellClass: "ag-center-aligned-cell",
    },
  ],
  [
    "dest",
    {
      headerName: "Dest",
      field: "dest",
      cellClass: "ag-center-aligned-cell",
    },
  ],
  [
    "details",
    {
      headerName: "Details",
      field: "details",
      cellClass: "ag-center-aligned-cell",
    },
  ],
  [
    "detail_expander",
    {
      headerName: "",
      field: "detail_expander",
      cellRenderer: "agGroupCellRenderer",
    },
  ],
  [
    "exchange",
    {
      headerName: "Exchange",
      field: "exchange",
      cellRenderer: VenueCell,
      cellClass: "ag-center-aligned-cell",
    },
  ],
  [
    "src_exchange",
    {
      headerName: "Source Exchange",
      field: "src_exchange",
      cellClass: "ag-center-aligned-cell",
    },
  ],
  [
    "dest_exchange",
    {
      headerName: "Destination Exchange",
      field: "dest_exchange",
      cellClass: "ag-center-aligned-cell",
    },
  ],
  [
    "exchange_misc_fee_amt",
    {
      headerName: "Exchange Misc. Fee Amount",
      field: "exchange_misc_fee_amt",
      valueFormatter: numericFormatterNoPrecision,
      comparator: customNumericSortFunc,
      cellClass: "ag-left-aligned-cell",
    },
  ],
  [
    "exchange_misc_fee_curr",
    {
      headerName: "Exchange Misc. Fee Currency",
      field: "exchange_misc_fee_curr",
      cellClass: "ag-left-aligned-cell",
    },
  ],
  [
    "exec_id",
    {
      headerName: "Execution ID",
      field: "exec_id",
      cellClass: "ag-center-aligned-cell",
    },
  ],
  [
    "exec_type",
    {
      headerName: "Execution Type",
      field: "exec_type",
      cellClass: "ag-center-aligned-cell",
    },
  ],
  [
    "executing_firm",
    {
      headerName: "Executing Firm",
      field: "executing_firm",
      cellClass: "ag-center-aligned-cell",
    },
  ],
  [
    "src_executing_firm",
    {
      headerName: "Source Executing Firm",
      field: "src_executing_firm",
      cellClass: "ag-center-aligned-cell",
    },
  ],
  [
    "dest_executing_firm",
    {
      headerName: "Destination Executing Firm",
      field: "dest_executing_firm",
      cellClass: "ag-center-aligned-cell",
    },
  ],
  [
    "id",
    {
      headerName: "ID",
      field: "id",
      cellClass: "ag-center-aligned-cell",
    },
  ],
  [
    "initial_margin",
    {
      headerName: "Initial Margin",
      field: "initial_margin",
      cellClass: "ag-center-aligned-cell",
    },
  ],
  [
    "intermediary",
    {
      headerName: "Intermediary",
      field: "intermediary",
      cellClass: "ag-center-aligned-cell",
    },
  ],
  [
    "src_intermediary",
    {
      headerName: "Source Intermediary",
      field: "src_intermediary",
      cellClass: "ag-center-aligned-cell",
    },
  ],
  [
    "dest_intermediary",
    {
      headerName: "Destination Intermediary",
      field: "dest_intermediary",
      cellClass: "ag-center-aligned-cell",
    },
  ],
  [
    "last_px",
    {
      headerName: "Last Price",
      field: "last_px",
      valueFormatter: (params: any) => currencyFormatter(params, "$"),
      comparator: customNumericSortFunc,
      cellClass: "ag-center-aligned-cell",
    },
  ],
  [
    "last_qty",
    {
      headerName: "Last Quantity",
      field: "last_qty",
      valueFormatter: numericFormatterNoPrecision,
      comparator: customNumericSortFunc,
      cellClass: "ag-center-aligned-cell",
    },
  ],
  [
    "leaves_qty",
    {
      headerName: "Leaves Quantity",
      field: "leaves_qty",
      valueFormatter: numericFormatterNoPrecision,
      comparator: customNumericSortFunc,
      cellClass: "ag-center-aligned-cell",
    },
  ],
  [
    "local_comm_misc_fee_amt",
    {
      headerName: "Local Commission Misc. Fee Amount",
      field: "local_comm_misc_fee_amt",
      valueFormatter: numericFormatterNoPrecision,
      comparator: customNumericSortFunc,
      cellClass: "ag-left-aligned-cell",
    },
  ],
  [
    "local_comm_misc_fee_curr",
    {
      headerName: "Local Commission Misc. Fee Currency",
      field: "local_comm_misc_fee_curr",
      cellClass: "ag-left-aligned-cell",
    },
  ],
  [
    "maintenance_margin",
    {
      headerName: "Maintenance Margin",
      field: "maintenance_margin",
      cellClass: "ag-center-aligned-cell",
    },
  ],
  [
    "margin_balance",
    {
      headerName: "Margin Balance",
      field: "margin_balance",
      valueFormatter: numericFormatterNoPrecision,
      comparator: customNumericSortFunc,
      cellClass: "ag-center-aligned-cell",
    },
  ],
  [
    "order_id",
    {
      headerName: "Order ID",
      field: "order_id",
      cellClass: "ag-center-aligned-cell",
    },
  ],
  [
    "order_qty",
    {
      headerName: "Order Quantity",
      field: "order_qty",
      valueFormatter: numericFormatterNoPrecision,
      comparator: customNumericSortFunc,
      cellClass: "ag-center-aligned-cell",
    },
  ],
  [
    "ord_type",
    {
      headerName: "Order Type",
      field: "ord_type",
      cellClass: "ag-center-aligned-cell",
    },
  ],
  [
    "placement_strategy",
    {
      headerName: "Placement Strategy",
      field: "placement_strategy",
      cellClass: "ag-center-aligned-cell",
    },
  ],
  [
    "pos_event_type",
    {
      headerName: "POS Event Type",
      field: "pos_event_type",
      cellClass: "ag-center-aligned-cell",
    },
  ],
  [
    "quantity",
    {
      headerName: "Quantity",
      field: "quantity",
      valueFormatter: numericFormatterNoPrecision,
      comparator: customNumericSortFunc,
      cellClass: "ag-center-aligned-cell",
    },
  ],
  [
    "price",
    {
      headerName: "$Price",
      field: "price",
      valueFormatter: (params: any) => currencyFormatter(params, "$"),
      comparator: customNumericSortFunc,
      cellClass: "ag-center-aligned-cell",
    },
  ],
  [
    "rpt_id",
    {
      headerName: "Report ID",
      field: "rpt_id",
      cellClass: "ag-center-aligned-cell",
    },
  ],
  [
    "rpt_type",
    {
      headerName: "Report Type",
      field: "rpt_type",
      cellClass: "ag-center-aligned-cell",
    },
  ],
  [
    "side",
    {
      headerName: "Side",
      field: "side",
      cellClass: "ag-center-aligned-cell",
      cellRenderer: SideCell,
    },
  ],
  [
    "src",
    {
      headerName: "Src",
      field: "src",
      cellClass: "ag-center-aligned-cell",
    },
  ],
  [
    "status",
    {
      headerName: "Status",
      field: "status",
      cellClass: "ag-center-aligned-cell",
      cellRenderer: OrderStatusCell,
      cellRendererParams: (params: any) => ({
        cum_qty: params?.data?.cum_qty || 0,
        order_qty: params?.data?.order_qty || 0,
      }),
    },
  ],
  [
    "symbol",
    {
      headerName: "Symbol",
      field: "symbol",
      cellClass: "ag-center-aligned-cell",
    },
  ],
  [
    "tax_misc_fee_amt",
    {
      headerName: "Tax Misc. Fee Amount",
      field: "tax_misc_fee_amt",
      valueFormatter: numericFormatterNoPrecision,
      comparator: customNumericSortFunc,
      cellClass: "ag-left-aligned-cell",
    },
  ],
  [
    "tax_misc_fee_curr",
    {
      headerName: "Tax Misc. Fee Currency",
      field: "tax_misc_fee_curr",
      cellClass: "ag-left-aligned-cell",
    },
  ],
  [
    "total_available_balance",
    {
      headerName: "Total Available Balance",
      field: "total_available_balance",
      valueFormatter: numericFormatterNoPrecision,
      comparator: customNumericSortFunc,
      cellClass: "ag-center-aligned-cell",
    },
  ],
  [
    "total_initial_margin",
    {
      headerName: "Total Initial Margin",
      field: "total_initial_margin",
      valueFormatter: numericFormatterNoPrecision,
      comparator: customNumericSortFunc,
      cellClass: "ag-center-aligned-cell",
    },
  ],
  [
    "total_long",
    {
      headerName: "Total Long",
      field: "total_long",
      valueFormatter: numericFormatterNoPrecision,
      comparator: customNumericSortFunc,
      cellClass: "ag-center-aligned-cell",
    },
  ],
  [
    "total_maintenance_margin",
    {
      headerName: "Total Maintenance Margin",
      field: "total_maintenance_margin",
      valueFormatter: numericFormatterNoPrecision,
      comparator: customNumericSortFunc,
      cellClass: "ag-center-aligned-cell",
    },
  ],
  [
    "total_margin_balance",
    {
      headerName: "Total Margin Balance",
      field: "total_margin_balance",
      valueFormatter: numericFormatterNoPrecision,
      comparator: customNumericSortFunc,
      cellClass: "ag-center-aligned-cell",
    },
  ],
  [
    "total_margin_ratio",
    {
      headerName: "Total Margin Ratio",
      field: "total_margin_ratio",
      valueFormatter: numericFormatterNoPrecision,
      comparator: customNumericSortFunc,
      cellClass: "ag-center-aligned-cell",
    },
  ],
  [
    "total_short",
    {
      headerName: "Total Short",
      field: "total_short",
      valueFormatter: numericFormatterNoPrecision,
      comparator: customNumericSortFunc,
      cellClass: "ag-center-aligned-cell",
    },
  ],
  [
    "total_unrealized_pnl",
    {
      headerName: "$Total Unrealized P&L",
      field: "total_unrealized_pnl",
      valueFormatter: (params: any) => currencyFormatter(params, "$"),
      comparator: customNumericSortFunc,
      cellClass: "ag-center-aligned-cell",
    },
  ],
  [
    "trade_id",
    {
      headerName: "Trade ID",
      field: "trade_id",
      cellClass: "ag-center-aligned-cell",
    },
  ],
  [
    "transact_time",
    {
      headerName: "Transact Time",
      field: "transact_time",
      cellClass: "ag-center-aligned-cell",
    },
  ],
  [
    "transfer_status",
    {
      headerName: "Transfer Status",
      field: "transfer_status",
      cellClass: "ag-center-aligned-cell",
      cellRenderer: TransferStatusCell,
      cellRendererParams: (params: any) => ({
        value: params?.data?.transfer_status || "",
      }),
    },
  ],
  [
    "transfer_type",
    {
      headerName: "Transfer Type",
      field: "transfer_type",
      cellClass: "ag-center-aligned-cell",
    },
  ],
  [
    "txid",
    {
      headerName: "Transact ID",
      field: "txid",
      cellClass: "ag-center-aligned-cell",
    },
  ],
  [
    "unrealized_pnl",
    {
      headerName: "$Unrealized P&L",
      field: "unrealized_pnl",
      valueFormatter: (params: any) => currencyFormatter(params, "$"),
      comparator: customNumericSortFunc,
      cellClass: "ag-center-aligned-cell",
    },
  ],
  [
    "updated",
    {
      headerName: "Updated",
      field: "updated",
      cellClass: "ag-center-aligned-cell",
    },
  ],
  [
    "venue",
    {
      headerName: "Venue",
      field: "venue",
      cellRenderer: VenueCell,
      cellClass: "ag-center-aligned-cell",
    },
  ],
]);

export const getColDefs = (colDefKeys: string[]): ColDef[] => {
  return colDefKeys
    .map((key) => initialColDefs.get(key))
    .filter((colDef) => colDef !== undefined) as ColDef[];
};
